import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";

import { InviteDialog } from "@/components/settings/invite-dialog";
import { SettingsSection } from "@/components/settings/settings-section";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/api";
import { can } from "@/lib/session";
import {
  changeStaffGroup,
  fetchGroups,
  fetchStaff,
  groupOptions,
  groupsQueryKey,
  initialsOf,
  removeStaff,
  staffQueryKey,
} from "@/lib/settings-api";
import type { StaffGroup, StaffMember } from "@/lib/settings-api";

interface MemberRowProps {
  canRemove: boolean;
  canUpdate: boolean;
  groups: StaffGroup[];
  isSelf: boolean;
  member: StaffMember;
  onChanged: () => void;
}

const MemberRow = ({
  canRemove,
  canUpdate,
  groups,
  isSelf,
  member,
  onChanged,
}: MemberRowProps) => {
  const groupItems = groupOptions(groups);
  const groupName =
    groups.find((group) => group.id === member.groupId)?.name ?? "Unknown";

  const changeGroup = async (groupId: string | null) => {
    if (!groupId || groupId === member.groupId) {
      return;
    }
    await toast
      .promise(changeStaffGroup(member, groupId), {
        error: apiErrorMessage,
        loading: "Updating group...",
        success: "Group updated",
      })
      .catch(() => null);
    onChanged();
  };

  const remove = async () => {
    await toast
      .promise(removeStaff(member), {
        error: apiErrorMessage,
        loading: "Removing...",
        success: `Removed ${member.email}`,
      })
      .catch(() => null);
    onChanged();
  };

  return (
    <li className="flex flex-col gap-3 border-b p-4 last:border-b-0 sm:flex-row sm:items-center sm:gap-4 sm:px-5">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          aria-hidden
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
        >
          {initialsOf(member.name || member.email)}
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="flex items-center gap-2 truncate text-sm font-medium">
            {member.name || member.email}
            {isSelf ? <Badge variant="outline">You</Badge> : null}
          </span>
          <span className="truncate text-sm text-muted-foreground">
            {member.email}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 pl-12 sm:pl-0">
        {canUpdate && !isSelf ? (
          <Select
            items={groupItems}
            onValueChange={changeGroup}
            value={member.groupId}
          >
            <SelectTrigger
              aria-label={`Group for ${member.email}`}
              className="w-44"
              size="sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {groupItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="secondary">{groupName}</Badge>
        )}

        {canRemove && !isSelf ? (
          <AlertDialog>
            <AlertDialogTrigger
              render={
                <Button
                  aria-label={`Remove ${member.email}`}
                  size="icon"
                  variant="ghost"
                >
                  <Trash2 aria-hidden />
                </Button>
              }
            />
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove {member.email}?</AlertDialogTitle>
                <AlertDialogDescription>
                  They’ll lose access to the admin straight away.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} variant="destructive">
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
      </div>
    </li>
  );
};

const memberCountLabel = (count: number): string =>
  count === 1 ? "1 person" : `${count} people`;

const settingsRoute = getRouteApi("/admin/settings");

const TeamPage = () => {
  const { session } = settingsRoute.useRouteContext();
  const queryClient = useQueryClient();

  const staffQuery = useQuery({ queryFn: fetchStaff, queryKey: staffQueryKey });
  const groupsQuery = useQuery({
    enabled: can(session, "groups:read"),
    queryFn: fetchGroups,
    queryKey: groupsQueryKey,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: staffQueryKey });

  const members = staffQuery.data ?? [];
  const groups = groupsQuery.data ?? [];
  const canInvite = can(session, "staff:invite") && groups.length > 0;

  return (
    <SettingsSection
      action={
        canInvite ? <InviteDialog groups={groups} onInvited={refresh} /> : null
      }
      description={
        staffQuery.isSuccess
          ? `${memberCountLabel(members.length)} can sign in to the admin.`
          : "Everyone who can sign in to the admin."
      }
      title="Team"
    >
      {staffQuery.isPending ? (
        <p className="p-5 text-sm text-muted-foreground">Loading team...</p>
      ) : (
        <ul>
          {members.map((member) => (
            <MemberRow
              canRemove={can(session, "staff:delete")}
              canUpdate={can(session, "staff:update") && groups.length > 0}
              groups={groups}
              isSelf={member.id === session.staff?.id}
              key={member.id}
              member={member}
              onChanged={refresh}
            />
          ))}
        </ul>
      )}
    </SettingsSection>
  );
};

export const Route = createFileRoute("/admin/settings/team")({
  component: TeamPage,
});
